import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEarthAmericas } from '@fortawesome/pro-light-svg-icons/faEarthAmericas';
import { getCountryName } from 'fym-shared/src/geo/getCountryName';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { Panel, PanelHead } from '../components/primitives/Panel';
import { ListRow } from '../components/primitives/ListRow';
import { DeltaPill } from '../components/primitives/DeltaPill';
import { SMLEmptyState } from '../components/SMLEmptyState/SMLEmptyState';
import { SMLUpgradeLockBanner } from '../components/SMLUpgradeLockBanner/SMLUpgradeLockBanner';
import { SMLUpgradeFeature } from '../config/music24PlanCapabilities';
import { asPercentFraction } from './marketsRange';
import { formatDelta } from './formatDelta';
import { MarketsError, TopCountriesOutput } from './types';
import styles from './TopMarketsCard.module.scss';

const messages = defineMessages({
  title: { id: 'app.sml.markets.countries.title', defaultMessage: 'Top markets' },
  noData: {
    id: 'app.sml.markets.countries.noData',
    defaultMessage: 'No country data yet. Follow a track or an artist and this fills in tomorrow.',
  },
  shareBarLabel: { id: 'app.sml.markets.countries.shareBarLabel', defaultMessage: '{share} of reach' },
});

export interface TopMarketsCardProps {
  data?: TopCountriesOutput;
  isLoading: boolean;
  error: MarketsError;
  retry: () => void;
  /** False shows the upsell in place of the rows. */
  canFilterGeographically: boolean;
  /** How many country rows to print. */
  rowLimit?: number;
}

/**
 * The Markets "Top markets" pane: rank, country code chip, country name, a
 * share bar and the delta against the previous window.
 */
const TopMarketsCardComponent: React.FC<TopMarketsCardProps> = ({
  data,
  isLoading,
  error,
  retry,
  canFilterGeographically,
  rowLimit,
}) => {
  const intl = useIntl();

  const countries = useMemo(() => data?.countries ?? [], [data?.countries]);
  const rows = useMemo(
    () => (rowLimit === undefined ? countries : countries.slice(0, rowLimit)),
    [countries, rowLimit]
  );
  const topShare = useMemo(() => rows.reduce((max, country) => Math.max(max, country.share), 0), [rows]);

  const renderBody = () => {
    if (!canFilterGeographically) {
      return (
        <div className="p-3">
          <SMLUpgradeLockBanner feature={SMLUpgradeFeature.geography} />
        </div>
      );
    }
    if (isLoading) {
      return <LoadingView small />;
    }
    if (error !== null) {
      return <ErrorView error={error} retry={retry} />;
    }
    if (countries.length === 0) {
      return <SMLEmptyState icon={faEarthAmericas} title={intl.formatMessage(messages.noData)} />;
    }
    return (
      <div>
        {rows.map((country, index) => {
          const share = intl.formatNumber(asPercentFraction(country.share), {
            style: 'percent',
            maximumFractionDigits: 1,
          });
          return (
            <ListRow
              key={country.code}
              rank={index + 1}
              media={<span className={styles.code}>{country.code}</span>}
              title={getCountryName(country.code, intl.locale)}
              trailing={
                <>
                  <div
                    className={styles.shareBar}
                    role="img"
                    aria-label={intl.formatMessage(messages.shareBarLabel, { share })}
                  >
                    <div
                      className={styles.shareFill}
                      style={{ width: `${topShare > 0 ? (country.share / topShare) * 100 : 0}%` }}
                    />
                  </div>
                  <span className={styles.share}>{share}</span>
                  <DeltaPill value={country.delta} label={formatDelta(intl, country.delta)} />
                </>
              }
            />
          );
        })}
      </div>
    );
  };

  return (
    <Panel className="h-100">
      <PanelHead icon={<FontAwesomeIcon icon={faEarthAmericas} />} title={intl.formatMessage(messages.title)} />
      {renderBody()}
    </Panel>
  );
};

export const TopMarketsCard = TypedMemo(TopMarketsCardComponent);
